import React from 'react';
import moment from 'moment';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import ReactSortableTree from '@nosferatu500/react-sortable-tree';
import {
  addForm,
  showAddFormModal,
  hideAddFormModal,
  hideDeleteFormModal,
  showDeleteFormModal,
  deleteFormFromTree,
  showOwnershipModal,
  hideOwnershipModal,
  takeQueueItem,
  hideImyTreeWithholding,
  saveImyTreeViewOptions,
  setPaymentTypes,
  setStructureId,
  GetLinkOperationStatus
} from 'actions';
import { ApiService } from 'services';

import { history } from 'containers/App';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import StatementTypes from 'common/Forms/StatementTypes';
import NotFoundPage from 'components/Errors/NotFoundPage';
import DeleteFormModal from 'components/Modals/DeleteFormModal';
import AddFormModal from 'components/Modals/AddFormModal';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';
import { FormViewButton } from 'components/Common/Buttons/FormViewButton';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import Banner from 'components/Common/Banner';
import LinkWhsAccountModal from 'components/Modals/LinkWhsAccountModal';

import ColourKey from './ColourKey';
import ImyNode from './imyNode';

class VisualiseW8Imy extends React.PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        formId: PropTypes.string.isRequired,
        accountId: PropTypes.string.isRequired,
        versionNumber: PropTypes.string
      }).isRequired
    }).isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.shape({
      showWithholding: PropTypes.bool,
      selectedClient: PropTypes.shape({
        isPiiProtectionEnabled: PropTypes.bool
      })
    }),
    isAddFormModalShown: PropTypes.bool.isRequired,
    isDeleteFormModalShown: PropTypes.bool,
    isDeletingForm: PropTypes.bool,
    deleteFormError: PropTypes.string,
    deleteFormResponse: PropTypes.number,
    formsToDelete: PropTypes.arrayOf(PropTypes.shape()),
    isOwnershipModalShown: PropTypes.bool,
    changeOwnershipItem: PropTypes.shape(),
    imyTreeViewOptions: PropTypes.shape({}),
    currentStructureId: PropTypes.number,
    isLinkingToImyStructureModalShown: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    settings: null,
    isDeleteFormModalShown: false,
    isDeletingForm: false,
    deleteFormError: null,
    deleteFormResponse: null,
    formsToDelete: null,
    isOwnershipModalShown: false,
    changeOwnershipItem: null,
    imyTreeViewOptions: {
      displayMode: 'all',
      showWithholdingRates: false
    },
    currentStructureId: 0,
    isLinkingToImyStructureModalShown: false
  };

  constructor(props) {
    super(props);

    this.state = {
      statementType: null,
      rawData: null,
      data: null,
      treeError: null,
      displayWithholdingOption: props.settings && props.settings.showWithholding,
      imyOptions: props.imyTreeViewOptions,
    };
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillMount() {
    this.fetchTree();
  }

  componentDidUpdate(prevProps, prevState) {
    const { deleteFormResponse, isDeletingForm, imyTreeViewOptions } = this.props;
    const { data, imyOptions } = this.state;

    if (prevProps.isDeletingForm && !isDeletingForm
      && prevProps.deleteFormResponse === null && deleteFormResponse !== null
      && (data == null || data.length === 0 || data[0] == null || data[0].id == null || parseInt(data[0].id, 10) !== deleteFormResponse)) {
      this.fetchTree();
    }

    /* eslint-disable react/prop-types */
    if (imyOptions.structureId !== imyTreeViewOptions.structureId) {
      this.updateOptions(imyTreeViewOptions);
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(hideImyTreeWithholding());
    dispatch(hideOwnershipModal());
    dispatch(setStructureId(0));
    this.storeImyOptions();
  }

  onTitleClick(node) {
    const { dispatch, permissions } = this.props;

    const wfStatus = node.workflowStatus != null && node.workflowStatus.toLowerCase();
    const form = {
      id: node.id,
      accountId: node.accountId,
      formType: node.formType.toLowerCase(),
      workflowStatus: node.workflowStatus,
      workflowUsername: node.workflowUsername,
      workflowUserFullName: node.workflowUserFullName
    };
    let formUrl = `/forms/${ node.formType }/${ node.id }`;
    if (node.accountId) {
      formUrl = `${ formUrl }?account=${ node.accountId }`;
    }
    if (FormViewButton.isUserFormOwner(node.workflowUsername) || wfStatus === 'complete') {
      history.push(formUrl);
    } else if (FormViewButton.showModal(permissions, form)) {
      dispatch(showOwnershipModal({
        id: form.id,
        accountId: form.accountId,
        formType: form.formType,
        currentUser: form.workflowUsername,
        currentUserFullName: form.workflowUserFullName
      }));
    } else {
      if (wfStatus.includes('initial') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer])) {
        dispatch(takeQueueItem('form', form.id, form.accountId));
      }

      if (wfStatus.includes('secondary') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfSecondaryReviewer])) {
        dispatch(takeQueueItem('form', form.id, form.accountId));
      }
    }
  }

  get isPooledStatementType() {
    const { statementType } = this.state;
    return statementType === StatementTypes.PooledWhdStatement || statementType === StatementTypes.QiPooled || statementType === StatementTypes.NqiPooled;
  }

  get isStructureValid() {
    const { rawData } = this.state;
    return rawData && rawData.length > 0 && rawData[0].isStructureValid;
  }

  getRowHeight() {
    const { imyOptions: { showWithholdingRates, selectedPaymentTypes }, displayWithholdingOption, data } = this.state;
    if (showWithholdingRates) {
      const includesStatesRates = this.areStateRatesIncluded(data[0]);
      return (220 + (selectedPaymentTypes.length * 16 * (includesStatesRates ? 3 : 1)));
    }
    return displayWithholdingOption ? 155 : 175;
  }

  storeImyOptions = () => {
    const { dispatch, currentStructureId } = this.props;
    const { imyOptions } = this.state;
    dispatch(saveImyTreeViewOptions(currentStructureId, imyOptions));
  };

  onPaymentTypesSelectChanged = (e) => {
    const { imyOptions: { structureId, displayMode, showWithholdingRates, selectedPaymentTypes, availablePaymentTypes } } = this.state;

    selectedPaymentTypes.push(e.target.value);
    availablePaymentTypes.splice(availablePaymentTypes.indexOf(e.target.value), 1);
    availablePaymentTypes.sort();

    const newOptions = {
      structureId,
      displayMode,
      showWithholdingRates,
      selectedPaymentTypes,
      availablePaymentTypes };

    this.setState({ imyOptions: newOptions }, () => this.refreshData());
  };

  onPaymentTypeRemoved = (paymentType) => {
    const { imyOptions: { structureId, displayMode, showWithholdingRates, selectedPaymentTypes, availablePaymentTypes } } = this.state;

    availablePaymentTypes.push(paymentType);
    selectedPaymentTypes.splice(selectedPaymentTypes.indexOf(paymentType), 1);
    availablePaymentTypes.sort();

    const newOptions = {
      structureId,
      displayMode,
      showWithholdingRates,
      selectedPaymentTypes,
      availablePaymentTypes
    };

    this.setState({ imyOptions: newOptions }, () => this.refreshData());
  };

  onClearPaymentTypes = () => {
    const { imyOptions: { structureId, displayMode, showWithholdingRates, selectedPaymentTypes, availablePaymentTypes } } = this.state;

    const temp = JSON.parse(JSON.stringify(selectedPaymentTypes));
    temp.forEach((paymentType, index) => {
      availablePaymentTypes.push(paymentType);
      selectedPaymentTypes.splice(selectedPaymentTypes.indexOf(paymentType), 1);
    });
    availablePaymentTypes.sort();

    const newOptions = {
      structureId,
      displayMode,
      showWithholdingRates,
      selectedPaymentTypes,
      availablePaymentTypes
    };

    this.setState({ imyOptions: newOptions }, () => this.refreshData());
  };

  applyPaymentTypeFilter = (node) => {
    const { imyOptions: { selectedPaymentTypes } } = this.state;

    if (node.children != null && node.children.length > 0) {
      node.children.forEach(child => this.applyPaymentTypeFilter(child));
    }

    node.filteredRates = [];
    if (node.rates != null && node.rates.length > 0) {
      node.rates.forEach((rate) => {
        if (selectedPaymentTypes.indexOf(rate.paymentType) > -1) {
          node.filteredRates.push(rate);
        }
      });
    }

    if (node.filteredRates != null) {
      this.addMissingFields(node);
    }

    return node;
  };

  // eslint-disable-next-line class-methods-use-this
  addMissingFields(obj) {
    if (obj != null) {
      obj.expanded = true;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  areStateRatesIncluded(data) {
    if (!data) {
      return false;
    }
    if (data.rates && data.rates.length > 0) {
      for (let i = 0; i < data.rates.length; i += 1) {
        if (data.rates[i].state) {
          return true;
        }
      }
    }
    if (data.children && data.children.length > 0) {
      for (let i = 0; i < data.children.length; i += 1) {
        if (this.areStateRatesIncluded(data.children[i])) {
          return true;
        }
      }
    }
    return false;
  }

  updateOptions(newOptions) {
    this.setState({ imyOptions: newOptions }, () => this.refreshData());
  }

  fetchTree() {
    const { match, dispatch } = this.props;
    const { imyOptions: { displayMode, showWithholdingRates } } = this.state;

    const transform = (array) => {
      array.forEach((item) => {
        if (item != null) {
          this.addMissingFields(item);
          if (item.children != null) {
            transform(item.children);
          }
        }
      });
    };

    ApiService
      .getW8ImyTreeStructure(match.params.formId, match.params.accountId, match.params.versionNumber)
      .then((result) => {
        const { data } = result;

        const root = {
          id: data.id,
          structureId: data.id,
          formId: data.formId,
          name: data.name,
          formType: data.formType,
          valid: data.valid,
          expired: data.expired,
          isFFI: data.isFFI,
          isLayer: data.isLayer,
          taxRegimeType: data.taxRegimeType,
          accountId: data.accountId,
          customerId: data.customerId,
          customerDbId: data.customerDbId,
          title: data.title,
          workflowStatus: data.workflowStatus,
          workflowUsername: data.workflowUsername,
          workflowUserFullName: data.workflowUserFullName,
          workflowPhase: data.workflowPhase,
          children: data.children,
          expanded: true,
          rates: data.rates,
          attributes: data.attributes,
          workflowComplete: data.workflowComplete,
          isStructureValid: data.attributes != null && Object.prototype.hasOwnProperty.call(data.attributes, 'Structure is') && data.attributes['Structure is'] === 'Valid',
          isWhsLinkingActive: data.isWhsLinkingActive,
          effectiveFrom: data.effectiveFrom,
          effectiveTo: data.effectiveTo
        };

        this.setState({ statementType: data.statementType });
        dispatch(setStructureId(root.structureId));
        if (root.isWhsLinkingActive) {
          dispatch(GetLinkOperationStatus(root.accountId));
        }

        transform(root.children);

        if (data.statementType === StatementTypes.PooledWhdStatement) {
          const newOptions = {
            structureId: root.structureId,
            displayMode,
            showWithholdingRates: showWithholdingRates && root.isStructureValid,
            selectedPaymentTypes: data.paymentTypes ? data.paymentTypes : [],
            availablePaymentTypes: []
          };
          dispatch(setPaymentTypes(root.structureId, newOptions));
        } else {
          const defaultSelectedTypes = ['Dividends - Ordinary(Private)', 'Dividends - Ordinary(Public)'];

          const newOptions = {
            structureId: root.structureId,
            displayMode,
            showWithholdingRates: showWithholdingRates && root.isStructureValid,
            selectedPaymentTypes: data.paymentTypes ? defaultSelectedTypes : [],
            availablePaymentTypes: data.paymentTypes ? data.paymentTypes.filter(x => !defaultSelectedTypes.includes(x)) : []
          };
          dispatch(setPaymentTypes(root.structureId, newOptions));
        }

        if (displayMode === 'imyOnly') {
          let imys = JSON.parse(JSON.stringify(root));
          imys = this.filterImys([imys]);
          this.setState({ rawData: [root], data: [imys[0]] }, () => {
            this.refreshData();
          });
        } else {
          this.setState({ rawData: [root], data: [root] }, () => {
            this.refreshData();
          });
        }
      }).catch((err) => {
        if (err.response && err.response.data) {
          this.setState({ data: null, treeError: err.response.data });
        } else {
          this.setState({
            data: null,
            treeError: {
              code: 502,
              message: 'Sorry, there was a problem getting the W-8IMY tree structure'
            }
          });
        }
      });
  }

  openAddFormModal(item) {
    const { dispatch } = this.props;
    dispatch(showAddFormModal(item, false));
  }

  addForms(parentForm, values, onlineForm) {
    const { dispatch } = this.props;
    dispatch(addForm(parentForm, values, onlineForm));
  }

  closeAddFormModal(item) {
    const { dispatch } = this.props;
    dispatch(hideAddFormModal(item));
    this.fetchTree();
  }

  closeDeleteFormModal() {
    const { dispatch } = this.props;
    dispatch(hideDeleteFormModal());
  }

  openDeleteFormModal(item) {
    const { dispatch } = this.props;
    dispatch(showDeleteFormModal([item]));
  }

  deleteForm() {
    const { dispatch, match, formsToDelete } = this.props;
    const { data } = this.state;

    if (!formsToDelete || !formsToDelete.length === 0) {
      return;
    }

    const [formToDelete] = formsToDelete;
    const redirectAfterDelete = (!data || (data.length === 1 && data[0].id === formToDelete.id) || parseInt(match.params.formId, 10) === formToDelete.id);
    dispatch(deleteFormFromTree(formToDelete.formType, formToDelete.id, redirectAfterDelete));
  }

  filterImys(forms) {
    const { imyOptions: { showWithholdingRates } } = this.state;
    const r = forms?.filter((o) => {
      const hasChildren = o.children && o.children.length > 0;
      if (o.children) o.children = this.filterImys(o.children);
      return ((hasChildren) || (o.formType != null && o.formType.startsWith('w8imy')) || (o.name && o.name.indexOf('Unknown Recipient') >= 0 && showWithholdingRates));
    });
    return r;
  }

  findForm(currentNode, name) {
    if (currentNode != null) {
      if (name === currentNode.name) {
        return currentNode;
      }
      if (currentNode.children != null) {
        // Use a for loop instead of forEach to avoid nested functions
        // Otherwise "return" will not work properly
        for (let i = 0; i < currentNode.children.length; i += 1) {
          const currentChild = currentNode.children[i];
          let result = null;
          if (currentChild != null) {
            // Search in the current child
            result = this.findForm(currentChild, name);
          }
          // Return the result if the node has been found
          if (result != null) {
            return result;
          }
        }
      }
      // The node has not been found and we have no more options
      return null;
    }
    return null;
  }

  toggleDisplayMode() {
    const { rawData, imyOptions: { structureId, showWithholdingRates, selectedPaymentTypes, availablePaymentTypes } } = this.state;

    this.setState((prevState) => {
      if (prevState.imyOptions.displayMode === 'imyOnly') {
        const imys = JSON.parse(JSON.stringify(rawData));
        imys[0] = this.applyPaymentTypeFilter(imys[0]);
        return {
          imyOptions: { structureId, displayMode: 'all', showWithholdingRates, selectedPaymentTypes, availablePaymentTypes },
          data: imys
        };
      }

      let imys = JSON.parse(JSON.stringify(rawData));
      imys = this.filterImys(imys);
      imys[0] = this.applyPaymentTypeFilter(imys[0]);
      return {
        imyOptions: { structureId, displayMode: 'imyOnly', showWithholdingRates, selectedPaymentTypes, availablePaymentTypes },
        data: imys
      };
    }, () => {
      this.storeImyOptions();
    });
  }

  toggleShowWithholding() {
    const { imyOptions: { structureId, displayMode, showWithholdingRates, selectedPaymentTypes, availablePaymentTypes } } = this.state;
    this.setState({
      imyOptions: { structureId, displayMode, showWithholdingRates: !showWithholdingRates, selectedPaymentTypes, availablePaymentTypes }
    }, () => {
      this.refreshData();
    });
  }

  refreshData() {
    const { imyOptions, rawData } = this.state;
    let imys = JSON.parse(JSON.stringify(rawData));
    if (imyOptions.displayMode === 'imyOnly') {
      imys = this.filterImys(imys);
    }
    if (imys != null && imys.length > 0) {
      imys[0] = this.applyPaymentTypeFilter(imys[0]);
    }
    this.addMissingFields(imys);
    this.setState({
      data: imys
    });
    this.storeImyOptions();
  }

  reAssignItem() {
    const { dispatch, changeOwnershipItem } = this.props;

    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeQueueItem('form', changeOwnershipItem.id, changeOwnershipItem.accountId));
      dispatch(hideOwnershipModal());
    }
  }

  continueFromOwnershipModal() {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    let redirectUrl = `/forms/${ changeOwnershipItem.formType.toLowerCase() }/${ changeOwnershipItem.id }`;
    if (changeOwnershipItem.accountId) {
      redirectUrl = `${ redirectUrl }?account=${ changeOwnershipItem.accountId }`;
    }
    history.push(redirectUrl);
  }

  closeFromOwnershipModal() {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal());
  }

  render() {
    const {
      data,
      rawData,
      displayWithholdingOption,
      treeError,
      imyOptions: {
        displayMode,
        showWithholdingRates,
        selectedPaymentTypes,
        availablePaymentTypes
      },
    } = this.state;
    const {
      permissions,
      isAddFormModalShown,
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      isOwnershipModalShown,
      changeOwnershipItem,
      match: {
        params: {
          versionNumber,
          accountId
        }
      },
      settings,
      isLinkingToImyStructureModalShown
    } = this.props;

    const showingVersion = versionNumber !== null && typeof versionNumber !== 'undefined';

    const versionUndocumented = rawData && rawData[0] && rawData[0].formId === 0;
    const versionExpired = rawData && rawData[0] && rawData[0].effectiveTo && moment(rawData[0].effectiveTo).isBefore(moment(), 'day');
    const versionExpiring = rawData && rawData[0] && rawData[0].effectiveTo && moment(rawData[0].effectiveTo).isSame(moment(), 'day');
    const versionCurrent = rawData && rawData[0] && rawData[0].effectiveTo && moment(rawData[0].effectiveTo).isAfter(moment(), 'day');
    const versionOpenEnded = rawData && rawData[0] && (!rawData[0].effectiveTo || rawData[0].effectiveTo.length === 0);

    return (
      <div className="visualise-w8-imy" key="imy" style={ { height: '100%' } } onScroll={ e => this.scroll(e) }>
        { treeError && (treeError.code !== 0
          ? <NotFoundPage caption="The tree was not found" />
          : <div className="toast toast-error">
            { treeError.message }
          </div>) }
        { !treeError && <div id="rst__displayModeContainer" className="columns rst-options">
          {!treeError && versionNumber && versionUndocumented && <div className="col-12">
            <Banner
              mainText={ `Withholding Statement: No signed W-8IMY covering the period from (${ rawData && rawData[0] && rawData[0].effectiveFrom }) to (${ rawData && rawData[0] && rawData[0].effectiveTo })` }
              subText="Withholding Rates are default rates"
              className="toast-red"
            />
          </div>}
          {!treeError && versionNumber && versionExpired && !versionUndocumented && <div className="col-12">
            <Banner
              mainText={ `Withholding Statement: Expired (${ rawData && rawData[0] && rawData[0].effectiveTo })` }
              subText="Withholding Rates as of the Effective To date"
              className="toast-red"
            />
          </div>}
          {!treeError && versionNumber && versionExpiring && <div className="col-12">
            <Banner
              mainText="Withholding Statement: Published (expires today)"
              subText="Withholding Rates as of the Effective To date"
              className="toast-red"
            />
          </div>}
          {!treeError && versionNumber && versionCurrent && <div className="col-12">
            <Banner
              mainText={ `Withholding Statement : Published (expires ${ rawData && rawData[0] && rawData[0].effectiveTo })` }
              className="toast-blue"
            />
          </div>}
          {!treeError && versionNumber && versionOpenEnded && <div className="col-12">
            <Banner
              mainText="Withholding Statement: Published (no expiry)"
              className="toast-blue"
            />
          </div>}
          { !treeError && !versionNumber && <div className="col-12">
            <Banner
              mainText="Withholding Statement: Draft"
              className="toast-grey"
            />
          </div> }
          <div className="col-8 form">
            <div className="form-group">
              <h4>Display Options:</h4>
              <label className="form-label" htmlFor="displayModeAll">
                <input id="displayModeAll" type="radio" name="displayModeAll" value="all" className="form-control" onChange={ () => this.toggleDisplayMode() } checked={ displayMode === 'all' } />
                View All
              </label>
              <label className="form-label" htmlFor="displayModeImy">
                <input id="displayModeImy" type="radio" name="displayModeImy" value="imyOnly" className="form-control" onChange={ () => this.toggleDisplayMode() } checked={ displayMode === 'imyOnly' } />
                View IMYs only
              </label>
              { displayWithholdingOption && this.isStructureValid && <label className="form-label" htmlFor="showWithholding">
                <input id="showWithholding" type="checkbox" name="showWithholding" value="true" className="form-control" onChange={ () => this.toggleShowWithholding() } checked={ showWithholdingRates } />
                Withholding Rates
              </label> }
              { showWithholdingRates && this.isStructureValid && availablePaymentTypes
              && <React.Fragment>
                <br />
                <label className="form-label mb-10 mt-10" htmlFor="paymentTypes">Payment Types:</label>
                <select name="paymentTypes" id="paymentTypes" className="form-select max-width-300" onChange={ e => this.onPaymentTypesSelectChanged(e) }>
                  <option value="Select Payment Type...">Select Payment Type...</option>
                  { availablePaymentTypes.map((value, index) => <option key={ value } value={ value }>{ value }</option>) }
                </select>
                { selectedPaymentTypes && selectedPaymentTypes.length > 0 && <button className="btn btn-primary ml-10" type="button" onClick={ () => this.onClearPaymentTypes() }>Clear All</button> }
                <br />
                <br />
                { selectedPaymentTypes.map((value, index) => <div className="chip large-chip" key={ value }>
                  { value }
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                  <div className="btn btn-clear" aria-label="Close" onClick={ () => this.onPaymentTypeRemoved(value) } />
                </div>) }
                </React.Fragment> }
              { data && !this.isStructureValid && <span className="toast toast-error text-center">
                The structure is invalid. Withholding rates cannot be displayed for invalid structures.
              </span> }
            </div>
          </div>
          <div className="col-4 form">
            <ColourKey />
          </div>
        </div> }
        {!settings.selectedClient.isPiiProtectionEnabled && <AddFormModal
          isShown={ isAddFormModalShown }
          onConfirmUpload={ (parentForm, values) => this.addForms(parentForm, values, false) }
          onCancel={ () => this.closeAddFormModal() }
        />}
        <DeleteFormModal
          isShown={ isDeleteFormModalShown }
          isDeletingForm={ isDeletingForm }
          onConfirmDelete={ () => this.deleteForm() }
          onCancel={ () => this.closeDeleteFormModal() }
          deleteFormError={ deleteFormError }
        />
        { changeOwnershipItem != null && <ChangeOwnershipModal
          isShown={ isOwnershipModalShown }
          onAssign={ () => this.reAssignItem() }
          onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
          onCancel={ () => this.closeFromOwnershipModal() }
          workflowRecord={ changeOwnershipItem }
        /> }
        { (data != null) && <ReactSortableTree
          treeData={ data }
          onChange={ newData => this.setState({ data: newData }) }
          rowHeight={ this.getRowHeight() }
          canDrag={ false }
          searchQuery=""
          scaffoldBlockPxWidth={ 100 }
          className={ `imy-tree ${ versionNumber ? 'version' : '' } ${ (showWithholdingRates ? 'withholding-tree' : 'basic-tree') }` }
          generateNodeProps={ (rowInfo) => {
            let cssStyles = '';
            if (rowInfo.node.name && rowInfo.node.name.indexOf('Unknown Recipient') >= 0) {
              cssStyles = 'rst__unknownRecipientNode';
            } else if (rowInfo.node.workflowPhase === 'initial') {
              cssStyles = 'rst__initialWorkflowNode';
            } else if (rowInfo.node.workflowPhase === 'secondary') {
              cssStyles = 'rst__secondaryWorkflowNode';
            } else if (rowInfo.node.valid !== true || rowInfo.node.expired === true) {
              cssStyles = 'rst__invalidNode';
            } else if (rowInfo.node.isLayer) {
              cssStyles = 'rst__branchNode';
            }
            const nodeProps = {
              title: (<ImyNode node={ rowInfo.node } showWithholdingRates={ showWithholdingRates } />),
              className: `rst__leafNode ${ cssStyles }`,
              onClick: (e) => {
                if (e.target && e.target.className && e.target.className === 'rst__rowTitleItem' && rowInfo.node.id) {
                  this.onTitleClick(rowInfo.node);
                }
              },
              buttons: []
            };

            if (rowInfo.node.id != null) {
              if (rowInfo.node.isLayer === true) {
                nodeProps.className = `${ cssStyles }`;
                nodeProps.buttons = [];
                if (!showingVersion
                  && (((AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteCompleteForms]) && rowInfo.node.workflowStatus?.trim().toLowerCase().includes('complete')))
                    || (AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteInReviewForms]) && !rowInfo.node.workflowStatus?.trim().toLowerCase().includes('complete')))) {
                  nodeProps.buttons.push(<button
                    type="button"
                    className="taina-icon-sm delete"
                    onClick={ () => this.openDeleteFormModal(rowInfo.node) }
                  ><span className="icon icon-delete" /></button>);
                }
                if (!settings.selectedClient.isPiiProtectionEnabled
                  && !showingVersion && !this.isPooledStatementType
                  && AccessControl.hasPermission(permissions, [ActivityPerms.scannedUpload, ActivityPerms.scannedWhdStmtBoUpload], true)) {
                  nodeProps.buttons.push(<button
                    type="button"
                    className="taina-icon-sm add"
                    onClick={ () => this.openAddFormModal(rowInfo.node) }
                  >ADD FORM</button>);
                }
              } else {
                nodeProps.buttons = [];
                if (!showingVersion
                  && (((AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteCompleteForms]) && rowInfo.node.workflowStatus?.trim().toLowerCase().includes('complete')))
                    || (AccessControl.hasPermission(permissions, [ActivityPerms.scannedDeleteInReviewForms]) && !rowInfo.node.workflowStatus?.trim().toLowerCase().includes('complete')))) {
                  nodeProps.buttons.push(<button
                    type="button"
                    className="taina-icon-sm delete"
                    onClick={ () => this.openDeleteFormModal(rowInfo.node) }
                  ><span className="icon icon-delete" /></button>);
                }
              }
            } else if (rowInfo.node.formType != null && rowInfo.node.formType.startsWith('w8imy')) {
              nodeProps.className = ` ${ cssStyles }`;
            }

            return nodeProps;
          } }
        /> }
        { accountId > 0 && isLinkingToImyStructureModalShown
          && <LinkWhsAccountModal
            key="LinkWhsAccount"
            isShown={ isLinkingToImyStructureModalShown }
            accountId={ accountId }
            reloadOnComplete={ false }
          />}
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const {
    auth: {
      permissions,
      settings
    },
    account: {
      isLinkingToImyStructureModalShown
    },
    forms: {
      isAddFormModalShown,
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      deleteFormResponse,
      formsToDelete,
      showRate,
      imyTreeViewOptions,
      currentStructureId
    },
    workflow: {
      isOwnershipModalShown,
      changeOwnershipItem
    }
  } = state;

  // Get Default Options
  let defaultOptions = imyTreeViewOptions.filter(x => x.structureId === 0);
  if (defaultOptions && defaultOptions.length >= 1) {
    defaultOptions = (defaultOptions.length >= 1) ? JSON.parse(JSON.stringify(defaultOptions[0])) : null;
  }

  let options = null;
  if (currentStructureId) {
    // Override with saved options
    options = imyTreeViewOptions.filter(x => parseInt(x.structureId, 10) === parseInt(currentStructureId, 10));
  }
  options = (options && options.length >= 1) ? options[0] : JSON.parse(JSON.stringify(defaultOptions));

  return {
    permissions,
    settings,
    isAddFormModalShown,
    isDeleteFormModalShown,
    isDeletingForm,
    deleteFormError,
    deleteFormResponse,
    formsToDelete,
    isOwnershipModalShown,
    changeOwnershipItem,
    showRate,
    imyTreeViewOptions: options,
    currentStructureId,
    isLinkingToImyStructureModalShown
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(VisualiseW8Imy, 'VisualiseW8Imy')));
